export const ORIGIN_TAGLINE = 'Run your salon. Keep everything.'

export type OriginPlanTier = 'trial' | 'starter' | 'pro' | 'enterprise'

export interface PricingLimits {
  maxStylists: number
  maxServices: number
  maxBookingsPerMonth: number
  customDomain: boolean
  marketingAutomation: boolean
  analytics: boolean
  advancedAnalytics: boolean
  templates: boolean
  premiumTemplates: boolean
  clientPortal: boolean
  contentVersioning: boolean
  seoTools: boolean
  integrations: boolean
  whiteLabel: boolean
  prioritySupport: boolean
  cmsEditor: boolean
}

export interface OriginPlanCopy {
  key: OriginPlanTier
  name: string
  priceLabel: string
  monthlyPrice: number
  periodLabel: string
  pricingDescription: string
  pricingPageFeatures: string[]
  landingFeatures: string[]
  adminSettingsFeatures: string[]
}

export const ORIGIN_PLAN_ORDER: OriginPlanTier[] = ['trial', 'starter', 'pro', 'enterprise']
export const ORIGIN_PAID_PLAN_ORDER: Array<Exclude<OriginPlanTier, 'trial'>> = [
  'starter',
  'pro',
  'enterprise',
]

export const ORIGIN_MONTHLY_PLAN_PRICES: Record<OriginPlanTier, number> = {
  trial: 0,
  starter: 39,
  pro: 99,
  enterprise: 249,
}

export const ORIGIN_PLAN_LIMITS: Record<OriginPlanTier, PricingLimits> = {
  trial: {
    maxStylists: 2,
    maxServices: 10,
    maxBookingsPerMonth: 50,
    customDomain: false,
    marketingAutomation: false,
    analytics: true,
    advancedAnalytics: false,
    templates: true,
    premiumTemplates: false,
    clientPortal: false,
    contentVersioning: false,
    seoTools: false,
    integrations: false,
    whiteLabel: false,
    prioritySupport: false,
    cmsEditor: false,
  },
  starter: {
    maxStylists: 5,
    maxServices: 25,
    maxBookingsPerMonth: 200,
    customDomain: false,
    marketingAutomation: false,
    analytics: true,
    advancedAnalytics: false,
    templates: true,
    premiumTemplates: false,
    clientPortal: true,
    contentVersioning: true,
    seoTools: true,
    integrations: false,
    whiteLabel: false,
    prioritySupport: false,
    cmsEditor: false,
  },
  pro: {
    maxStylists: 20,
    maxServices: 100,
    maxBookingsPerMonth: 2000,
    customDomain: true,
    marketingAutomation: true,
    analytics: true,
    advancedAnalytics: true,
    templates: true,
    premiumTemplates: true,
    clientPortal: true,
    contentVersioning: true,
    seoTools: true,
    integrations: true,
    whiteLabel: false,
    prioritySupport: true,
    cmsEditor: false,
  },
  enterprise: {
    maxStylists: Infinity,
    maxServices: Infinity,
    maxBookingsPerMonth: Infinity,
    customDomain: true,
    marketingAutomation: true,
    analytics: true,
    advancedAnalytics: true,
    templates: true,
    premiumTemplates: true,
    clientPortal: true,
    contentVersioning: true,
    seoTools: true,
    integrations: true,
    whiteLabel: true,
    prioritySupport: true,
    cmsEditor: true,
  },
}

export const ORIGIN_PLAN_COPY: Record<OriginPlanTier, OriginPlanCopy> = {
  trial: {
    key: 'trial',
    name: 'Trial',
    priceLabel: 'Free',
    monthlyPrice: 0,
    periodLabel: '14 days',
    pricingDescription: 'Full access to launch, test fit, and publish without a credit card.',
    pricingPageFeatures: [
      '2 stylists',
      '50 bookings/mo',
      '10 services',
      '500 MB storage',
      'Free templates only',
      'Basic analytics',
    ],
    landingFeatures: [],
    adminSettingsFeatures: [],
  },
  starter: {
    key: 'starter',
    name: 'Starter',
    priceLabel: '$39',
    monthlyPrice: 39,
    periodLabel: '/month',
    pricingDescription: 'For solo stylists, booth renters, and small salons getting live fast.',
    pricingPageFeatures: [
      '5 stylists',
      '200 bookings/mo',
      '25 services',
      '2 GB storage',
      'Client portal',
      'SEO tools',
      'Basic analytics',
    ],
    landingFeatures: [
      'Up to 5 stylists',
      '25 services · 200 bookings/mo',
      'Client portal',
      'Content versioning',
      'SEO tools',
      'Basic analytics',
    ],
    adminSettingsFeatures: ['5 stylists', '200 bookings/mo', '25 services', 'Client portal'],
  },
  pro: {
    key: 'pro',
    name: 'Pro',
    priceLabel: '$99',
    monthlyPrice: 99,
    periodLabel: '/month',
    pricingDescription:
      'For established salons ready for automation, integrations, and deeper insight.',
    pricingPageFeatures: [
      '20 stylists',
      '2,000 bookings/mo',
      '100 services',
      '10 GB storage',
      'Custom domain',
      'Free + Premium',
      'Advanced analytics',
      'Marketing automation',
      'Integrations',
      'Priority support',
    ],
    landingFeatures: [
      'Up to 20 stylists',
      '100 services · 2,000 bookings/mo',
      'Custom domain',
      'Premium templates',
      'Advanced analytics',
      'Marketing automation',
      'Integrations · Priority support',
    ],
    adminSettingsFeatures: [
      '20 stylists',
      '2,000 bookings/mo',
      'Custom domain',
      'Marketing automation',
    ],
  },
  enterprise: {
    key: 'enterprise',
    name: 'Enterprise',
    priceLabel: '$249',
    monthlyPrice: 249,
    periodLabel: '/month',
    pricingDescription:
      'For multi-location operators that need brand control, scale, and concierge support.',
    pricingPageFeatures: [
      'Unlimited stylists',
      'Unlimited bookings',
      'Unlimited services',
      '50 GB storage',
      'Free + Premium',
      'Advanced analytics',
      'White-label + full CMS',
      'All integrations',
      'Priority support',
    ],
    landingFeatures: [
      'Unlimited stylists & services',
      'Unlimited bookings',
      'White-label branding',
      'CMS editor',
      'All integrations',
      'Priority support',
    ],
    adminSettingsFeatures: [
      'Unlimited stylists',
      'Unlimited bookings',
      'White-label + full CMS',
      'Priority support',
    ],
  },
}

export function normalizeOriginPlanTier(value: string | null | undefined): OriginPlanTier {
  if (value === 'starter' || value === 'pro' || value === 'enterprise' || value === 'trial') {
    return value
  }

  return 'trial'
}
